import { useCallback, useEffect, useRef } from 'react';

import isClient from 'commons/isClient';

// GTM owns loading site.js — we just wait for it to appear.
// Poll every 200ms, give up after 20s (100 attempts).
const POLL_INTERVAL_MS = 200;
const POLL_MAX_ATTEMPTS = 100;

// Shared across all hook instances — only one poller runs at a time.
const state = {
  ready: false,
  callbacks: [],
};

const isSdkReady = () =>
  isClient() &&
  typeof window.crl8 === 'object' &&
  window.crl8 !== null &&
  (typeof window.crl8.createExperience === 'function' ||
    (window.crl8.$BVHelpers &&
      typeof window.crl8.$BVHelpers.createExperience === 'function'));

let pollerTimer = null;
let pollAttempts = 0;

const flushCallbacks = () => {
  state.ready = true;
  state.callbacks.forEach(cb => cb());
  state.callbacks = [];
};

const startPolling = () => {
  if (pollerTimer !== null || state.ready) return;
  pollAttempts = 0;

  pollerTimer = setInterval(() => {
    pollAttempts += 1;
    if (isSdkReady()) {
      clearInterval(pollerTimer);
      pollerTimer = null;
      flushCallbacks();
    } else if (pollAttempts >= POLL_MAX_ATTEMPTS) {
      clearInterval(pollerTimer);
      pollerTimer = null;
      state.callbacks = [];
    }
  }, POLL_INTERVAL_MS);
};

// Returns an unsubscribe so callers can dequeue a still-pending callback.
//
// Why this matters: if the SDK hasn't loaded yet, createExperience pushes its
// `run` onto state.callbacks and returns. If the consumer then unmounts (e.g.
// fast PDP→PDP navigation before site.js arrives), the next mount queues its
// own run for the same containerId. When the SDK finally loads, flushCallbacks
// fires both — the second create lands on a container that's already been
// initialized, and crl8 emits the duplicate-init error this whole hook exists
// to prevent (JOYBIRD-40-5PR8, 157K events). Returning an unsubscribe lets the
// hook drop the stale callback in destroyExperience before it can fire.
// See PR #6740 discussion r3287267076.
const whenReady = callback => {
  if (state.ready && isSdkReady()) {
    callback();
    return () => {};
  }
  state.callbacks.push(callback);
  startPolling();
  return () => {
    const i = state.callbacks.indexOf(callback);
    if (i !== -1) state.callbacks.splice(i, 1);
  };
};

const useCuralate = () => {
  const unsubscribeRef = useRef(null);

  useEffect(() => {
    if (!isClient()) return undefined;
    if (isSdkReady()) {
      state.ready = true;
    } else {
      startPolling();
    }
    return undefined;
  }, []);

  // Pass the SDK the container ELEMENT, never an id string: on unmount the
  // node is already out of the DOM, so an id lookup finds nothing and the
  // failed destroy leaves the experience registered, blocking every later
  // create as a duplicate. The container must carry `data-crl8-container-id`.
  const createExperience = useCallback(({ container }) => {
    if (!isClient() || !container) return;

    if (unsubscribeRef.current) unsubscribeRef.current();

    const run = () => {
      unsubscribeRef.current = null;
      const { crl8 } = window;
      if (!crl8) return;
      const create =
        crl8.createExperience ||
        (crl8.$BVHelpers && crl8.$BVHelpers.createExperience);
      if (typeof create === 'function') {
        try {
          // On fresh loads the SDK's auto-scan creates the gallery first, so
          // this rejects as an expected duplicate (JOYBIRD-40-5PR8).
          Promise.resolve(create(container)).catch(() => {});
        } catch (_) {
          // SDK logs its own errors; swallow to keep React tree healthy.
        }
      }
    };

    unsubscribeRef.current = whenReady(run);
  }, []);

  // Idempotent — safe to call if the SDK never loaded.
  const destroyExperience = useCallback(({ container }) => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    if (!isClient() || !container) return;
    const { crl8 } = window;
    if (!crl8) return;
    const destroy =
      crl8.destroyExperience ||
      (crl8.$BVHelpers && crl8.$BVHelpers.destroyExperience);
    if (typeof destroy === 'function') {
      try {
        Promise.resolve(destroy(container)).catch(() => {});
      } catch (_) {
        // see createExperience
      }
    }
  }, []);

  return { createExperience, destroyExperience };
};

export default useCuralate;
